import { Alert, Stack } from "@mantine/core";
import { useEffect } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useVariantSites } from "../../../../hooks/useVariantSites";
import { DateRangeInput } from "../../../../mantine/dates/DateRangeInput/DateRangeInput";
import { AlertPresets } from "../../../../mantine/mantinePresets";
import { YesNoChoice } from "../../../../types/api.types";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { luxonDateTimeToBackendDateOrDateTimeOrNull } from "../../../../utils/dates/luxonDateTimeToBackendDateOrDateTimeOrNull";
import { FormFieldController } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { CustomFormFieldWrapper } from "../../../BuildingBlocks/Forms/MantineFormFields/CustomFormFieldWrapper/CustomFormFieldWrapper";
import { AnlageStromstgList } from "../../../StromStg/AnlageStromstgList/AnlageStromstgList";
import { GENERATOR_FORM_DATA } from "../GeneratorModal.constants";
import type { GeneratorSingleEndpointResponse } from "../GeneratorModal.types";

interface StromStgTabProps {
  anlageStromstgId?: number | null;
  generatorForm: UseFormReturn<GeneratorSingleEndpointResponse>;
  variantId: number;
}

type YesNoChoiceWithEmpty = YesNoChoice | "" | undefined | null; //type hack maybe until dropdown is better https://github.com/node-energy/optinode_backend/pull/13113

const EMPTY_WARNING_MESSAGE =
  "Diese Angabe ist erforderlich, damit eine Stromsteueranmeldung erstellt werden kann. Bitte wählen Sie eine Antwort aus.";

function StromStgTab({
  anlageStromstgId,
  generatorForm,
  variantId
}: StromStgTabProps) {
  const { control, formState, watch, setValue } = generatorForm;

  const choice911 = watch(
    "anlage_stromstg.stfrei_paragraph_9_abs_1_nr_1_stromstg"
  ) as YesNoChoiceWithEmpty;
  const choiceEinzelerlaubnis: YesNoChoiceWithEmpty = watch(
    "anlage_stromstg.einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung"
  ) as YesNoChoiceWithEmpty;
  const choice913a = watch(
    "anlage_stromstg.stromstg_paragraph_9_1_3a"
  ) as YesNoChoiceWithEmpty;
  const choice913b = watch(
    "anlage_stromstg.stromstg_paragraph_9_1_3b"
  ) as YesNoChoiceWithEmpty;

  const hiddenFields = watch("anlage_stromstg.hidden_fields");

  const einzelerlaubnisAnswerUnchanged =
    choiceEinzelerlaubnis ===
    formState.defaultValues?.anlage_stromstg
      ?.einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung;
  const datumEinzelerlaubnisHiddenByBackend =
    einzelerlaubnisAnswerUnchanged &&
    hiddenFields?.includes(
      "datum_einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung"
    );

  useEffect(() => {
    if (choice913a === YesNoChoice.no) {
      setValue(
        GENERATOR_FORM_DATA[
          "anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3a"
        ].name,
        null
      );
    }
  }, [choice913a, setValue]);
  useEffect(() => {
    if (choice913b === YesNoChoice.no) {
      setValue(
        GENERATOR_FORM_DATA[
          "anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3b"
        ].name,
        null
      );
    }
  }, [choice913b, setValue]);
  useEffect(() => {
    if (choice911 === YesNoChoice.no) {
      setValue(
        GENERATOR_FORM_DATA[
          "anlage_stromstg.datum_erlaubnis_stfrei_paragraph_9_abs_1_nr_1_stromstg"
        ].name,
        null
      );
    }
  }, [choice911, setValue]);
  useEffect(() => {
    if (choiceEinzelerlaubnis === YesNoChoice.no) {
      setValue(
        GENERATOR_FORM_DATA[
          "anlage_stromstg.datum_einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung"
        ].name,
        null
      );
    }
  }, [choiceEinzelerlaubnis, setValue]);

  const { sites } = useVariantSites(variantId);
  const showHasStandortuebergreifendeVerklammerung = sites.length > 1;
  return (
    <Stack gap={0}>
      {showHasStandortuebergreifendeVerklammerung &&
        !hiddenFields?.includes("has_standortuebergreifende_verklammerung") && (
          <FormFieldController
            control={control}
            data={
              GENERATOR_FORM_DATA[
                "anlage_stromstg.has_standortuebergreifende_verklammerung"
              ]
            }
          />
        )}
      {anlageStromstgId && <AnlageStromstgList id={anlageStromstgId} />}

      <Alert
        color={AlertPresets.warning.color}
        icon={AlertPresets.warning.icon}
        mb="lg"
      >
        Achtung: Bitte nehmen Sie aktuell keine Änderungen an der
        Versorgererlaubnis oder den Stromsteuerbefreiungen vor, die sich aus der
        Stromsteuernovelle für das Meldejahr 2026 ergeben. Wir arbeiten derzeit
        an der Umsetzung dieser Änderungen in opti.node. Eigenständige
        Anpassungen könnten zu fehlerhaften Stromsteueranmeldungen führen.{" "}
        <br /> <br />
        Sobald die Software auf dem neuesten Stand ist und die Änderungen
        eingetragen werden können, melden wir uns bei Ihnen. <br /> <br />
        Bei Fragen wenden Sie sich gerne an unseren Support.
      </Alert>
      {!hiddenFields?.includes("stfrei_paragraph_9_abs_1_nr_1_stromstg") && (
        <FormFieldController
          control={control}
          data={{
            ...GENERATOR_FORM_DATA[
              "anlage_stromstg.stfrei_paragraph_9_abs_1_nr_1_stromstg"
            ],
            warning: choice911 === "" ? EMPTY_WARNING_MESSAGE : undefined
          }}
        />
      )}
      {choice911 === YesNoChoice.yes &&
        !hiddenFields?.includes(
          "datum_erlaubnis_stfrei_paragraph_9_abs_1_nr_1_stromstg"
        ) && (
          <FormFieldController
            control={control}
            data={{
              ...GENERATOR_FORM_DATA[
                "anlage_stromstg.datum_erlaubnis_stfrei_paragraph_9_abs_1_nr_1_stromstg"
              ],
              required: choice911 === YesNoChoice.yes
            }}
          />
        )}

      {!hiddenFields?.includes(
        "einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung"
      ) && (
        <FormFieldController
          control={control}
          data={{
            ...GENERATOR_FORM_DATA[
              "anlage_stromstg.einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung"
            ],
            warning:
              choiceEinzelerlaubnis === "" ? EMPTY_WARNING_MESSAGE : undefined
          }}
        />
      )}
      {choiceEinzelerlaubnis === YesNoChoice.yes &&
        !datumEinzelerlaubnisHiddenByBackend && (
          <FormFieldController
            control={control}
            data={{
              ...GENERATOR_FORM_DATA[
                "anlage_stromstg.datum_einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung"
              ],
              required: true
            }}
          />
        )}

      {!hiddenFields?.includes("stromstg_paragraph_9_1_3a") && (
        <FormFieldController
          control={control}
          data={{
            ...GENERATOR_FORM_DATA["anlage_stromstg.stromstg_paragraph_9_1_3a"],
            warning: choice913a === "" ? EMPTY_WARNING_MESSAGE : undefined
          }}
        />
      )}
      {choice913a === YesNoChoice.yes &&
        !hiddenFields?.includes("zeitraum_stromstg_paragraph_9_1_3a") && (
          <Controller
            control={control}
            name={
              GENERATOR_FORM_DATA[
                "anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3a"
              ].name
            }
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomFormFieldWrapper
                {...GENERATOR_FORM_DATA[
                  "anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3a"
                ]}
                error={error}
                required={choice913a === YesNoChoice.yes}
              >
                <DateRangeInput
                  hasError={!!error}
                  value={[
                    backendDateOrDateTimeToLuxonDateTimeOrNull(value?.lower) ||
                      undefined,
                    backendDateOrDateTimeToLuxonDateTimeOrNull(value?.upper) ||
                      undefined
                  ]}
                  onChange={(newRange) => {
                    onChange(
                      newRange[0]
                        ? {
                            lower: luxonDateTimeToBackendDateOrDateTimeOrNull(
                              newRange[0]
                            ),
                            upper: luxonDateTimeToBackendDateOrDateTimeOrNull(
                              newRange[1]
                            )
                          }
                        : null
                    );
                  }}
                />
              </CustomFormFieldWrapper>
            )}
            rules={{ required: choice913a === YesNoChoice.yes }}
          />
        )}

      {!hiddenFields?.includes("stromstg_paragraph_9_1_3b") && (
        <FormFieldController
          control={control}
          data={{
            ...GENERATOR_FORM_DATA["anlage_stromstg.stromstg_paragraph_9_1_3b"],
            warning: choice913b === "" ? EMPTY_WARNING_MESSAGE : undefined
          }}
        />
      )}
      {choice913b === YesNoChoice.yes &&
        !hiddenFields?.includes("zeitraum_stromstg_paragraph_9_1_3b") && (
          <Controller
            control={control}
            name={
              GENERATOR_FORM_DATA[
                "anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3b"
              ].name
            }
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomFormFieldWrapper
                {...GENERATOR_FORM_DATA[
                  "anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3b"
                ]}
                error={error}
                required={choice913b === YesNoChoice.yes}
              >
                <DateRangeInput
                  hasError={!!error}
                  value={[
                    backendDateOrDateTimeToLuxonDateTimeOrNull(value?.lower) ||
                      undefined,
                    backendDateOrDateTimeToLuxonDateTimeOrNull(value?.upper) ||
                      undefined
                  ]}
                  onChange={(newRange) => {
                    onChange(
                      newRange[0]
                        ? {
                            lower: luxonDateTimeToBackendDateOrDateTimeOrNull(
                              newRange[0]
                            ),
                            upper: luxonDateTimeToBackendDateOrDateTimeOrNull(
                              newRange[1]
                            )
                          }
                        : null
                    );
                  }}
                />
              </CustomFormFieldWrapper>
            )}
            rules={{ required: choice913b === YesNoChoice.yes }}
          />
        )}

      {!hiddenFields?.includes("hauptzollamt_anlagennummer") && (
        <FormFieldController
          control={control}
          data={
            GENERATOR_FORM_DATA["anlage_stromstg.hauptzollamt_anlagennummer"]
          }
        />
      )}
    </Stack>
  );
}

export { StromStgTab };
